import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { fetchSS12Export, fetchSS12InvoiceRunReport } from '../../felles/APICalls_SS12';
import Utils from '../../felles/Utils';
import FirmNavnId from '../../models/FirmNavnId';
import { SS12InvoiceRunReport, SS12InvoiceRunRow, SS12ProductGroupRow } from '../../models/SS12InvoiceRunReport';
import * as actions from '../../store/actions/index';
import { AppState } from '../../store/rootReducer';
import StorePicker, { storeIds, storeNames } from '../SS10/components/StorePicker';
import MonthPicker from './MonthPicker';

// ─── Formatting (user story: whole kroner with a space as thousands separator, "+4,2 %") ─────────────

const amountFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const percentFormat = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatAmount = (v: number | null | undefined) => (v == null ? '–' : amountFormat.format(Math.round(v)));
const formatSignedAmount = (v: number | null | undefined) => (v == null ? '–' : (Math.round(v) > 0 ? '+' : '') + formatAmount(v));
const formatSignedPercent = (v: number | null | undefined) => (v == null ? '–' : (v > 0 ? '+' : '') + percentFormat.format(v) + ' %');
const formatPercent = (v: number | null | undefined) => (v == null ? '–' : percentFormat.format(v) + ' %');
const isNegative = (v: number | null | undefined) => v != null && Math.round(v * 10) < 0;

// ─── Styles (compact, like NO04 / NR01) ───────────────────────────────────────────────────────

const CARD_SX = { p: 1.5, borderRadius: 2 } as const;
// The store list the same compact height as NO04's fields (34px, 0.85rem); StorePicker itself is shared with SS10.
const COMPACT_FIELD_SX = { '& .MuiInputBase-root': { minHeight: 34, py: '0 !important', fontSize: '0.85rem' } } as const;
const TABLE_SX = {
  '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.82rem', whiteSpace: 'nowrap' },
  '& .MuiTableCell-head': { fontWeight: 600, color: 'text.secondary', backgroundColor: 'background.paper' },
} as const;
const RIGHT_ALIGNED = { align: 'right' } as const;
const NEGATIVE_RED = { color: '#c62828' } as const;
const EXPORT_BUTTON_SX = { height: 30, fontSize: '0.8rem', textTransform: 'none' } as const;
// A product group with an account (VGR-KONTO): ID and name in red, as in SS03.
const ACCOUNT_RED = { color: '#c62828' } as const;

// ─── Sorting the product group table (every column but the name); the sum row stays last ─────────

type SortColumn = 'productGroup' | 'nettoThisYear' | 'nettoLastYear' | 'difference' | 'changePercent'
  | 'grossProfitPercent' | 'accumulatedThisYear' | 'accumulatedLastYear' | 'accumulatedChangePercent';
interface Sort { by: SortColumn; descending: boolean }

// The API's order: highest netto this year first.
const DEFAULT_SORT: Sort = { by: 'nettoThisYear', descending: true };

/** Same rules as InvoiceRunReportBuilder.Sort (Excel / PDF): empty percentages last, ties by ID. */
const sortRows = (rows: SS12ProductGroupRow[], sort: Sort) => {
  const direction = sort.descending ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (sort.by === 'productGroup') return direction * a.productGroup.localeCompare(b.productGroup);
    const x = a[sort.by];
    const y = b[sort.by];
    if (x == null || y == null) return x == null && y == null ? a.productGroup.localeCompare(b.productGroup) : x == null ? 1 : -1;
    return direction * (x - y) || a.productGroup.localeCompare(b.productGroup);
  });
};

// ─── Searching the product group table: ID or name contains the text ─────────
// A trailing * means "starts with": "1*" gives every product group whose ID (or name) starts with 1.

const matchesSearch = (row: SS12ProductGroupRow, search: string) => {
  const text = search.trim().toLowerCase();
  if (!text) return true;
  const id = row.productGroup.trim().toLowerCase();
  const name = row.name.toLowerCase();
  if (text.endsWith('*')) {
    const prefix = text.replace(/\*+$/, '');
    return id.startsWith(prefix) || name.startsWith(prefix);
  }
  return id.includes(text) || name.includes(text);
};

// Same rules as InvoiceRunReportBuilder: no percentage when the base is 0 kr.
const changePercent = (thisYear: number, lastYear: number) =>
  Math.round(lastYear) === 0 ? null : ((thisYear - lastYear) / Math.abs(lastYear)) * 100;

/** The sum row of the rows the search shows: "Sum 4 av 312 varegrupper", BF % weighted. */
const sumOf = (rows: SS12ProductGroupRow[], total: number): SS12ProductGroupRow => {
  const add = (pick: (r: SS12ProductGroupRow) => number) => rows.reduce((s, r) => s + pick(r), 0);
  const nettoThisYear = add((r) => r.nettoThisYear);
  const nettoLastYear = add((r) => r.nettoLastYear);
  const grossProfitThisYear = add((r) => r.grossProfitThisYear);
  const accumulatedThisYear = add((r) => r.accumulatedThisYear);
  const accumulatedLastYear = add((r) => r.accumulatedLastYear);
  return {
    productGroup: '',
    name: `Sum ${rows.length} av ${total} varegrupper`,
    hasAccount: false,
    nettoThisYear,
    nettoLastYear,
    difference: nettoThisYear - nettoLastYear,
    changePercent: changePercent(nettoThisYear, nettoLastYear),
    grossProfitThisYear,
    grossProfitPercent: Math.round(nettoThisYear) === 0 ? null : (grossProfitThisYear / nettoThisYear) * 100,
    accumulatedThisYear,
    accumulatedLastYear,
    accumulatedChangePercent: changePercent(accumulatedThisYear, accumulatedLastYear),
  };
};

function SortableHeader({ column, label, sort, onSort, right = false }: {
  column: SortColumn; label: string; sort: Sort; onSort: (column: SortColumn) => void; right?: boolean;
}) {
  const active = sort.by === column;
  return (
    <TableCell {...(right ? RIGHT_ALIGNED : {})} sortDirection={active ? (sort.descending ? 'desc' : 'asc') : false}>
      <TableSortLabel active={active} direction={active && !sort.descending ? 'asc' : 'desc'} onClick={() => onSort(column)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
}


export default function SS12InvoiceRuns() {
  const dispatch = useDispatch();
  const userJWT = useSelector((state) => (state as AppState).auth.userJWT);
  const firmListFromAdmin: FirmNavnId[] = useSelector((state) => (state as AppState).userAdmin.firmListFromAdmin);
  const isFetchingAdminFirmData: boolean = useSelector((state) => (state as AppState).userAdmin.isFetchingAdminFirmData);

  // The logged-in company is the main company (hovedfirma) and the default selection, as in SS10.
  const mainCompany = new FirmNavnId(Number(Utils.T_companyNr), userJWT?.CompanyName ?? '', 0);

  const [stores, setStores] = useState<FirmNavnId[]>([mainCompany]);
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [report, setReport] = useState<SS12InvoiceRunReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** 0 = the whole month so far (the total row), 1-4 = one run. */
  const [selectedRun, setSelectedRun] = useState(0);
  const [showAccumulated, setShowAccumulated] = useState(true);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [search, setSearch] = useState('');

  // The same column again flips the direction; a new column starts with the highest first (the ID lowest first).
  const changeSort = (column: SortColumn) =>
    setSort((s) => (s.by === column ? { by: column, descending: !s.descending } : { by: column, descending: column !== 'productGroup' }));

  // Excel / PDF of what the page shows: month, stores, selected run and the visible columns.
  const downloadExport = (format: 'excel' | 'pdf') => {
    setExporting(format);
    setError('');
    fetchSS12Export(format, {
      month: monthKey,
      butikkId: storeKey,
      storeNames: storeNames(stores),
      run: selectedRun,
      showAccumulated,
      sortBy: sort.by,
      sortDescending: sort.descending,
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Salg-pr-mnd-${monthKey}${selectedRun > 0 ? '-kjoring-' + selectedRun : ''}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Kunne ikke lage filen. Prøv igjen.'))
      .finally(() => setExporting(null));
  };

  useEffect(() => {
    if (!isFetchingAdminFirmData && (firmListFromAdmin ?? []).length === 0) dispatch(actions.firmListFromDBGet());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthKey = month.format('YYYYMM');
  const storeKey = storeIds(stores);

  // Month or store changed: load both tables again. Selecting a run needs no call - every invoiced
  // run's product group table is in the response.
  useEffect(() => {
    if (!storeKey) {
      // The answer to a request still running is ignored, so stop its loading state here.
      setReport(null);
      setLoading(false);
      setError('');
      return;
    }
    let active = true;   // ignore the answer to an older request
    setLoading(true);
    setError('');
    fetchSS12InvoiceRunReport(monthKey, storeKey)
      .then((res) => {
        if (!active) return;
        if (res.data.message && res.data.message !== 'OK') setError(res.data.message);
        setReport(res.data);
        setSelectedRun((run) => (res.data.productGroupTables.some((t) => t.run === run) ? run : 0));
      })
      .catch(() => {
        if (!active) return;
        // Don't leave the previous month's or stores' figures under the new selection's heading and export.
        setReport(null);
        setError('Kunne ikke hente faktureringen. Prøv igjen.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [monthKey, storeKey]);

  const monthName = month.locale('nb').format('MMMM YYYY');
  // While a new month loads the old figures stay on screen, so their titles keep the old month.
  const reportMonthName = report ? dayjs(`${report.month.slice(0, 4)}-${report.month.slice(4)}-01`).locale('nb').format('MMMM YYYY') : monthName;
  const storeName = storeNames(stores);
  const table = report?.productGroupTables.find((t) => t.run === selectedRun)
    ?? report?.productGroupTables.find((t) => t.run === 0);
  const shownRows = (table?.rows ?? []).filter((row) => matchesSearch(row, search));
  const tableTitle = !table || !report
    ? ''
    : table.run > 0
      ? table.title
      : (report.lastInvoicedRun === 4 ? 'Hele ' : 'Hittil i ') + reportMonthName;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.5, letterSpacing: 1 }}>
          Rapporter salg
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
          Salg pr. mnd.
        </Typography>
      </Box>

      {/* Filter: store(s) and one month (no from-to). */}
      <Paper variant="outlined" sx={{ ...CARD_SX, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '0 1 380px', minWidth: 260, ...COMPACT_FIELD_SX }}>
          <Typography variant="caption" color="text.secondary">Butikk liste</Typography>
          <StorePicker compact mainCompany={mainCompany} companies={firmListFromAdmin ?? []} value={stores} onChange={setStores} />
        </Box>
        <Box sx={{ flex: '0 0 180px' }}>
          <Typography variant="caption" color="text.secondary">Måned</Typography>
          <MonthPicker value={month} onChange={setMonth} maxDate={dayjs()} />
        </Box>
      </Paper>

      {/* Reading a whole year of invoice lines takes a few seconds: say so, and keep the old figures (dimmed) until the new ones are here. */}
      {loading && (
        <Box role="status" aria-live="polite">
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Henter fakturering for {monthName}… dette kan ta noen sekunder.
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {!storeKey && <Alert severity="info">Velg minst én butikk.</Alert>}
      {report && storeKey && !loading && report.lastInvoicedRun === 0 && (
        <Alert severity="info">
          {/* Not the total row: with no run invoiced it compares against no runs of last year either (always 0). */}
          {report.runs.every((r) => Math.round(r.nettoLastYear) === 0)
            ?`Ingen fakturering i ${reportMonthName}, verken i år eller samme måned i fjor.`
            : `Ingen kjøringer fakturert ennå i ${reportMonthName} – fjorårstallene vises.`}
        </Alert>
      )}

      {report && storeKey && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }} aria-busy={loading}>
          {/* Invoice runs */}
          <Paper variant="outlined" sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', px: 1.5, py: 1 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Fakturakjøringer {reportMonthName} · {storeName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Klikk en fakturert kjøring for å se den alene</Typography>
                <FormControlLabel
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                  control={<Checkbox size="small" checked={showAccumulated} onChange={(e) => setShowAccumulated(e.target.checked)} />}
                  label="Vis akkumulert"
                />
                <Button variant="outlined" size="small" startIcon={<GridOnOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                  disabled={exporting !== null || loading} onClick={() => downloadExport('excel')}>
                  {exporting === 'excel' ? 'Lager…' : 'Excel'}
                </Button>
                <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                  disabled={exporting !== null || loading} onClick={() => downloadExport('pdf')}>
                  {exporting === 'pdf' ? 'Lager…' : 'PDF'}
                </Button>
              </Box>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ ...TABLE_SX, minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Kjøring</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell {...RIGHT_ALIGNED}>Netto i år</TableCell>
                    <TableCell {...RIGHT_ALIGNED}>Netto i fjor</TableCell>
                    <TableCell {...RIGHT_ALIGNED}>Diff.</TableCell>
                    <TableCell {...RIGHT_ALIGNED}>%</TableCell>
                    {showAccumulated && <TableCell {...RIGHT_ALIGNED}>Akkumulert i år</TableCell>}
                    {showAccumulated && <TableCell {...RIGHT_ALIGNED}>Akkumulert i fjor</TableCell>}
                    {showAccumulated && <TableCell {...RIGHT_ALIGNED}>Akk. %</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...report.runs, report.total].map((row) => (
                    <RunRow
                      key={row.run}
                      row={row}
                      invoicedRunCount={report.invoicedRunCount}
                      isTotal={row.run === 0}
                      selected={selectedRun === row.run}
                      showAccumulated={showAccumulated}
                      onSelect={() => row.invoiced && setSelectedRun(row.run)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Product groups for the selected run or the month so far */}
          <Paper variant="outlined" sx={{ ...CARD_SX, p: 0, flex: '1 0 320px', minHeight: 320, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', px: 1.5, py: 1 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Salg pr. varegruppe · {tableTitle}
              </Typography>
              {/* Only filters the screen: Excel and PDF always have every product group. */}
              <TextField
                size="small"
                placeholder="Søk varegruppe (ID eller navn, f.eks. 1*)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 300, '& .MuiInputBase-root': { height: 30, fontSize: '0.85rem' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Tøm søk" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ ...TABLE_SX, minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <SortableHeader column="productGroup" label="Varegruppe ID" sort={sort} onSort={changeSort} />
                    <TableCell>Varegruppe Navn</TableCell>
                    <SortableHeader column="nettoThisYear" label="Netto i år" sort={sort} onSort={changeSort} right />
                    <SortableHeader column="nettoLastYear" label="Netto i fjor" sort={sort} onSort={changeSort} right />
                    <SortableHeader column="difference" label="Diff." sort={sort} onSort={changeSort} right />
                    <SortableHeader column="changePercent" label="%" sort={sort} onSort={changeSort} right />
                    <SortableHeader column="grossProfitPercent" label="BF %" sort={sort} onSort={changeSort} right />
                    {showAccumulated && <SortableHeader column="accumulatedThisYear" label="Akkumulert i år" sort={sort} onSort={changeSort} right />}
                    {showAccumulated && <SortableHeader column="accumulatedLastYear" label="Akkumulert i fjor" sort={sort} onSort={changeSort} right />}
                    {showAccumulated && <SortableHeader column="accumulatedChangePercent" label="Akk. %" sort={sort} onSort={changeSort} right />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortRows(shownRows, sort).map((row) => (
                    <ProductGroupRow key={row.productGroup} row={row} showAccumulated={showAccumulated} />
                  ))}
                  {table && shownRows.length > 0 && (
                    <ProductGroupRow row={search ? sumOf(shownRows, table.rows.length) : table.sum} showAccumulated={showAccumulated} isSum />
                  )}
                  {shownRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showAccumulated ? 10 : 7} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        {table && table.rows.length > 0
                          ? `Ingen varegrupper passer til «${search.trim()}».`
                          : `Ingen varegrupper med salg i ${reportMonthName}.`}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

// Status chips as in the mockup: invoiced runs pink, runs still to come blue.
const INVOICED_CHIP_SX = { backgroundColor: '#fde7ef', color: '#9c1f4a', border: '1px solid #f5b5c9', '& .MuiChip-icon': { color: '#9c1f4a' } } as const;
const PLANNED_CHIP_SX = { backgroundColor: '#e8f1fd', color: '#1756a9', border: '1px solid #b6d0f5', '& .MuiChip-icon': { color: '#1756a9' } } as const;
const CHIP_SX = { height: 22, fontSize: '0.75rem', fontWeight: 500 } as const;
const TOTAL_ROW_COLOR = '#8e1b4a';

function RunStatus({ row, invoicedRunCount }: { row: SS12InvoiceRunRow; invoicedRunCount: number }) {
  if (row.run === 0)
    return <Box component="span" sx={{ color: 'text.secondary' }}>{invoicedRunCount} av 4 fakturert</Box>;
  return row.invoiced
    ? <Chip size="small" icon={<ReceiptLongOutlinedIcon />} label="Fakturert" sx={{ ...CHIP_SX, ...INVOICED_CHIP_SX }} />
    : <Chip size="small" icon={<ScheduleOutlinedIcon />} label="Planlagt" sx={{ ...CHIP_SX, ...PLANNED_CHIP_SX }} />;
}

function RunRow({ row, invoicedRunCount, isTotal, selected, showAccumulated, onSelect }: {
  row: SS12InvoiceRunRow; invoicedRunCount: number; isTotal: boolean; selected: boolean; showAccumulated: boolean; onSelect: () => void;
}) {
  const bold = isTotal ? { fontWeight: 700 } : {};
  const RadioIcon = selected ? RadioButtonCheckedIcon : RadioButtonUncheckedIcon;
  return (
    // Invoiced runs (and the total) can also be reached with Tab and selected with Enter or Space.
    <TableRow
      hover={row.invoiced}
      selected={selected}
      onClick={onSelect}
      tabIndex={row.invoiced ? 0 : -1}
      aria-selected={selected}
      aria-disabled={!row.invoiced}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        cursor: row.invoiced ? 'pointer' : 'default',
        ...(isTotal ? { '& .MuiTableCell-root': { borderTop: '2px solid', borderTopColor: 'divider' } } : {}),
        '&.Mui-selected': { boxShadow: 'inset 3px 0 0 #1976d2' },
        '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: -2 },
      }}
    >
      <TableCell sx={bold}>
        {isTotal ? (
          // The total row selects the whole month: a radio mark like the mockup.
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: TOTAL_ROW_COLOR, fontWeight: 700 }}>
            <RadioIcon sx={{ fontSize: '1rem' }} />{row.label}
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ fontWeight: 600 }}>{row.label}</Box>
            {row.invoiceDate && (
              <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>{dayjs(row.invoiceDate).format('DD.MM.YYYY')}</Box>
            )}
          </>
        )}
      </TableCell>
      <TableCell><RunStatus row={row} invoicedRunCount={invoicedRunCount} /></TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={bold}>{formatAmount(row.nettoThisYear)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={bold}>{formatAmount(row.nettoLastYear)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={{ ...bold, ...(isNegative(row.difference) ? NEGATIVE_RED : {}) }}>{formatSignedAmount(row.difference)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={isNegative(row.changePercent) ? NEGATIVE_RED : {}}>{formatSignedPercent(row.changePercent)}</TableCell>
      {showAccumulated && <TableCell {...RIGHT_ALIGNED} sx={bold}>{formatAmount(row.accumulatedThisYear)}</TableCell>}
      {showAccumulated && <TableCell {...RIGHT_ALIGNED} sx={bold}>{formatAmount(row.accumulatedLastYear)}</TableCell>}
      {showAccumulated && (
        <TableCell {...RIGHT_ALIGNED} sx={isNegative(row.accumulatedChangePercent) ? NEGATIVE_RED : {}}>{formatSignedPercent(row.accumulatedChangePercent)}</TableCell>
      )}
    </TableRow>
  );
}

function ProductGroupRow({ row, showAccumulated, isSum = false }: {
  row: SS12ProductGroupRow; showAccumulated: boolean; isSum?: boolean;
}) {
  const sumSx = isSum ? { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'divider' } : {};
  return (
    <TableRow hover={!isSum}>
      <TableCell sx={{ ...sumSx, ...(row.hasAccount ? ACCOUNT_RED : {}) }}>{row.productGroup}</TableCell>
      <TableCell sx={{ ...sumSx, ...(row.hasAccount ? ACCOUNT_RED : {}) }}>{row.name}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={sumSx}>{formatAmount(row.nettoThisYear)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={sumSx}>{formatAmount(row.nettoLastYear)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={{ ...sumSx, ...(isNegative(row.difference) ? NEGATIVE_RED : {}) }}>{formatSignedAmount(row.difference)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={{ ...sumSx, ...(isNegative(row.changePercent) ? NEGATIVE_RED : {}) }}>{formatSignedPercent(row.changePercent)}</TableCell>
      <TableCell {...RIGHT_ALIGNED} sx={sumSx}>{formatPercent(row.grossProfitPercent)}</TableCell>
      {showAccumulated && <TableCell {...RIGHT_ALIGNED} sx={sumSx}>{formatAmount(row.accumulatedThisYear)}</TableCell>}
      {showAccumulated && <TableCell {...RIGHT_ALIGNED} sx={sumSx}>{formatAmount(row.accumulatedLastYear)}</TableCell>}
      {showAccumulated && (
        <TableCell {...RIGHT_ALIGNED} sx={{ ...sumSx, ...(isNegative(row.accumulatedChangePercent) ? NEGATIVE_RED : {}) }}>{formatSignedPercent(row.accumulatedChangePercent)}</TableCell>
      )}
    </TableRow>
  );
}
